/**
 * The cruise drive (plan R17, §2.9 flow 4; spec §4.2): mass-lock, the 2 s charge, engaging (the ship detaches and
 * moves kinematically in float64 on R6's law), the cruise tick with its alignment assist, and the drops (auto within
 * the target's drop distance, `targetDropM`: `dropDistanceM`, or `stationDropFromHullM` off a station's hull; manual
 * on a J tap; emergency inside a shell or another station's hull; the interdicted drop is in interdiction.ts). Every drop points the velocity along the nose at the drop speed, reattaches the ship and emits
 * `REV.cruiseDrop`.
 *
 * `stepNormal` and `stepCruise` run per tick and allocate nothing (D-014); engaging and dropping are rare events.
 * Floats cross calls only inside object fields (see kinematics.ts).
 */
import { SIM_DT } from '../../core/loop'
import { emitReach, REV } from '../../reach/events'
import { MASS_LOCK_REASONS, type ReachCore } from '../../reach/runtime/types'
import { detachShip, type Sim } from '../../sim'
import { Pos64 } from '../../world/components'
import { cruiseAllowed, massLockOf } from '../math'
import { cruiseChargeSFor, cruiseLagSFor } from '../config'
import { bandOfShip, fillMassLockShips, hostileLockOn, readNose, shipFromEcs, shipToEcs, speedOf, turnDetached } from './flight'
import { rollInterdiction } from './interdiction'
import { measureShip, stepCruiseShip, TARGET_NONE, TARGET_POINT, targetDropM, writeEta } from './kinematics'
import { checkLaneEntry } from './lanes'
import { CHARGE, DROP, type TravelRuntime } from './state'
import { checkRelay } from './transit'
import { cancelCharge, dropOut } from './transitions'

/** Float slack so 120 ticks of 1/60 s reach a 2 s charge exactly. */
const CHARGE_EPS = 1e-6

/** `travel.targetDistance` and `etaS` for the ship at its `Pos64`, from the cached nav target (0 with none). */
function measureTarget(rt: TravelRuntime, core: ReachCore, eid: number): void {
  const t = core.travel
  const g = rt.target
  if (g.shape === TARGET_NONE) {
    t.targetDistance = 0
    t.etaS = 0
    return
  }
  const dx = g.x - Pos64.x[eid]
  const dy = g.y - Pos64.y[eid]
  const dz = g.z - Pos64.z[eid]
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
  t.targetDistance = g.shape === TARGET_POINT ? d : d > g.radius ? d - g.radius : 0
  writeEta(t, g, rt.configs.travel)
}

/** The mass-lock at the ship's `Pos64` into `travel.massLock` and `massLockIndex` (ships and the lock are refreshed first). */
function updateMassLock(rt: TravelRuntime, core: ReachCore, eid: number, hostile: boolean): void {
  const input = rt.massLock
  if (input === null) return
  const t = core.travel
  t.massLock = massLockOf(Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], input, hostile, rt.configs.travel, rt.hit)
  t.massLockIndex = rt.hit.index
}

/** `REV.massLockDenied` with the current reason, at the ship. */
function denied(core: ReachCore, eid: number): void {
  emitReach(core.events, REV.massLockDenied, 0, MASS_LOCK_REASONS.indexOf(core.travel.massLock), Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], 0)
}

/** Engages the drive (rare): the ship detaches and cruises along its nose at its current speed. */
export function engage(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): void {
  const t = core.travel
  const speed = speedOf(eid)
  detachShip(sim, eid)
  shipFromEcs(rt, eid)
  const ship = rt.ship
  ship.speed = speed
  let allowed = 0
  if (rt.massLock !== null) {
    measureShip(ship, rt.target, rt.massLock)
    allowed = cruiseAllowed(ship.dTarget, ship.dSurface, rt.target.shape !== TARGET_NONE, rt.configs.travel)
  }
  const n = rt.nose
  ship.vx = n.x * speed
  ship.vy = n.y * speed
  ship.vz = n.z * speed
  shipToEcs(rt, eid)
  t.mode = 'cruise'
  t.chargeS = 0
  t.speed = speed
  t.allowed = allowed
  t.massLock = 'none'
  t.massLockIndex = -1
  rt.rollTimer = 0
  emitReach(core.events, REV.cruiseEngage, 0, 0, ship.x, ship.y, ship.z, allowed)
}

/**
 * Normal flight and the charge, per tick: the mass-lock, the relay hint and transit, lane entry, then the charge.
 * A J press starts the charge (refused during a cooldown; refused with `REV.massLockDenied` when mass-locked).
 * Holding J accumulates `chargeS`; releasing it or a mass-lock appearing cancels it; at `cruise.chargeS` the drive
 * engages.
 */
export function stepNormal(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): void {
  const t = core.travel
  if (rt.massLock === null) return
  readNose(rt, eid)
  fillMassLockShips(rt, sim, eid)
  updateMassLock(rt, core, eid, hostileLockOn(sim, eid))
  t.speed = speedOf(eid)
  t.allowed = 0
  t.chargeNeedS = cruiseChargeSFor(rt.configs.travel, bandOfShip(sim, eid))
  measureTarget(rt, core, eid)
  // M10 P5a10a (review C1): in normal flight only a ship carried in an L's cradle has no body; it can't charge, take
  // a relay or a lane (cruise would give it a body inside the L).
  if (sim.physics.bodies[eid] === undefined) {
    if (t.mode === 'charging') cancelCharge(core, eid)
    return
  }
  if (checkRelay(rt, core, sim, eid)) return
  if (checkLaneEntry(rt, core, sim, eid)) return
  const ri = core.input
  if (t.mode === 'normal') {
    if (!ri.cruisePressed || t.cooldownS > 0) return
    if (t.massLock !== 'none') {
      denied(core, eid)
      return
    }
    t.mode = 'charging'
    t.chargeS = 0
    emitReach(core.events, REV.cruiseCharge, CHARGE.start, 0, Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], 0)
  }
  if (!ri.cruiseHeld) {
    cancelCharge(core, eid)
    return
  }
  if (t.massLock !== 'none') {
    t.mode = 'normal'
    t.chargeS = 0
    denied(core, eid)
    return
  }
  t.chargeS += SIM_DT
  if (t.chargeS >= t.chargeNeedS - CHARGE_EPS) engage(rt, core, sim, eid)
}

/**
 * One cruise tick: turn at the cruise rate, a J tap drops (manual cooldown), else the ship moves on the law; within
 * the target's drop distance it drops (auto), inside a shell or another station's hull it drops (emergency cooldown),
 * otherwise the interdiction roll runs.
 */
export function stepCruise(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): void {
  const t = core.travel
  const c = rt.configs.travel.cruise
  const input = rt.massLock
  if (input === null) return
  turnDetached(rt, sim, eid)
  if (core.input.cruisePressed) {
    dropOut(rt, core, sim, eid, DROP.manual, c.dropSpeed, c.manualCooldownS)
    return
  }
  const ship = rt.ship
  shipFromEcs(rt, eid)
  ship.speed = t.speed
  ship.throttle = t.throttle
  ship.lagS = cruiseLagSFor(rt.configs.travel, bandOfShip(sim, eid))
  stepCruiseShip(ship, rt.nose, rt.heading, rt.target, input, rt.configs.travel)
  shipToEcs(rt, eid)
  t.speed = ship.speed
  t.allowed = ship.allowed
  const hasTarget = rt.target.shape !== TARGET_NONE
  t.targetDistance = hasTarget ? ship.dTarget : 0
  if (hasTarget) writeEta(t, rt.target, rt.configs.travel)
  else t.etaS = 0
  if (hasTarget && ship.dTarget <= targetDropM(rt.target, rt.configs.travel)) dropOut(rt, core, sim, eid, DROP.auto, c.dropSpeed, 0)
  else if (ship.dSurface <= 0) dropOut(rt, core, sim, eid, DROP.emergency, c.dropSpeed, c.emergencyCooldownS)
  else rollInterdiction(rt, core, sim, eid)
}
